import {
  ADDRESS,
  EMAIL,
  GEO_LAT,
  GEO_LNG,
  GOOGLE_PLACE_LINK,
  INSTAGRAM_LINK,
  LOGO_URL,
  OG_IMAGE,
  PHONE_TEL,
  SITE_URL,
  TRIPADVISOR_LINK,
  WHATSAPP_LINK,
} from "@/pages/home/constants";

const CONTEXT = "https://schema.org";

export const localBusinessSchema = {
  "@context": CONTEXT,
  "@type": "TravelAgency",
  "@id": `${SITE_URL}#business`,
  name: "Eco Tour Turismo",
  alternateName: ["Eco Tour Turismo Capitólio", "Eco Tour Capitólio"],
  url: SITE_URL,
  image: [OG_IMAGE, LOGO_URL],
  logo: LOGO_URL,
  telephone: PHONE_TEL,
  email: EMAIL,
  description:
    "Eco Tour Turismo é especializada em passeios de lancha e experiências náuticas em Capitólio, no Lago de Furnas, coração do Mar de Minas. Oferece passeios compartilhados e exclusivos, aluguel de lancha, passeio de jet ski e roteiros pelos cânions, Lagoa Azul e Vale dos Tucanos.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "MG-050, km 305",
    addressLocality: "Capitólio",
    addressRegion: "MG",
    postalCode: "37930-000",
    addressCountry: "BR",
    name: ADDRESS,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: GEO_LAT,
    longitude: GEO_LNG,
  },
  hasMap: GOOGLE_PLACE_LINK,
  priceRange: "R$ 100 – R$ 700",
  areaServed: [
    { "@type": "City", name: "Capitólio" },
    { "@type": "AdministrativeArea", name: "Minas Gerais" },
    { "@type": "Place", name: "Lago de Furnas" },
    { "@type": "Place", name: "Mar de Minas" },
  ],
  knowsAbout: [
    "passeio de lancha em Capitólio",
    "passeios náuticos em Capitólio",
    "passeio no Lago de Furnas",
    "aluguel de lancha em Capitólio",
    "passeio de jet ski em Capitólio",
    "Mar de Minas",
  ],
  sameAs: [SITE_URL, INSTAGRAM_LINK, TRIPADVISOR_LINK, GOOGLE_PLACE_LINK],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: PHONE_TEL,
    contactType: "reservas",
    areaServed: "BR",
    availableLanguage: "Portuguese",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Passeios e experiências náuticas em Capitólio",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Passeio de lancha compartilhado em Capitólio",
          description:
            "Passeio de lancha em grupo pelo Lago de Furnas, com cerca de 3 horas pelos principais atrativos de Capitólio.",
          areaServed: { "@type": "Place", name: "Lago de Furnas, Capitólio" },
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Passeio de lancha exclusivo e privativo em Capitólio",
          description:
            "Passeio de lancha privativo para famílias e grupos, com roteiro e horário de saída personalizados no Lago de Furnas.",
          areaServed: { "@type": "Place", name: "Lago de Furnas, Capitólio" },
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Passeio e aluguel de jet ski em Capitólio",
          description:
            "Passeio guiado e locação de jet ski no Lago de Furnas, no Mar de Minas, com paradas para fotos e banho.",
          areaServed: { "@type": "Place", name: "Lago de Furnas, Capitólio" },
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Aluguel e locação de lancha em Capitólio",
          description:
            "Locação de lancha particular em Capitólio para grupos, famílias e turmas, com e sem marinheiro.",
          areaServed: { "@type": "Place", name: "Lago de Furnas, Capitólio" },
        },
      },
    ],
  },
  potentialAction: {
    "@type": "ReserveAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: WHATSAPP_LINK,
      inLanguage: "pt-BR",
      actionPlatform: [
        "http://schema.org/DesktopWebPlatform",
        "http://schema.org/MobileWebPlatform",
      ],
    },
    result: { "@type": "Reservation", name: "Reserva de passeio de lancha em Capitólio" },
  },
};

export const websiteSchema = {
  "@context": CONTEXT,
  "@type": "WebSite",
  "@id": `${SITE_URL}#website`,
  url: SITE_URL,
  name: "Eco Tour Turismo — Passeios de Lancha em Capitólio",
  inLanguage: "pt-BR",
  publisher: { "@id": `${SITE_URL}#business` },
};

export const homeWebPageSchema = {
  "@context": CONTEXT,
  "@type": "WebPage",
  "@id": `${SITE_URL}#webpage`,
  url: SITE_URL,
  name: "Passeio de Lancha em Capitólio | Eco Tour Turismo",
  description:
    "Passeios de lancha em Capitólio no Lago de Furnas: passeio compartilhado e exclusivo, aluguel de lancha, passeio de jet ski e roteiros pelos Cânions de Capitólio e pela Lagoa Azul.",
  inLanguage: "pt-BR",
  isPartOf: { "@id": `${SITE_URL}#website` },
  about: { "@id": `${SITE_URL}#business` },
  primaryImageOfPage: OG_IMAGE,
};

export function buildBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildFaqSchema(items: { question: string; answer: string }[]) {
  return {
    "@context": CONTEXT,
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}